/**
 * The secured-floor template: a button on the Channel Modifiers step that
 * opens a dialog asking for the three channels the interlock joins, and hands
 * the complete schema-v8 modifier (`securedFloorModifier`) to the step, which
 * loads it into the form unsaved for the operator to review and save.
 *
 * Every control carries `data-doc-label`, so the documentation scanner keys it
 * by its caption rather than by its position.
 */
import React, { useState } from "react";
import { Alert, Button, Input, Modal, Radio, Select } from "antd";
import { useTranslation } from "react-i18next";
import { Control } from "../../../../flatbuffers/generated/control/control";
import type { ChannelModifierT } from "../../../../flatbuffers/generated/archive/channel-modifier";
import {
  securedFloorModifier,
  type SecuredFloorVariant,
} from "../../../../flatbuffers/builders/securedFloorTemplate";

interface SecuredFloorTemplateModalProps {
  /** The channels a modifier on this server may use. */
  channels: { id: string; name: string }[];
  /** Receives the built modifier; the step loads it into the form, unsaved. */
  onApply: (modifier: ChannelModifierT) => void;
}

/** The name a new secured-floor modifier starts with. */
export function defaultTemplateName(variant: SecuredFloorVariant): string {
  return `Secured floor (${variant} call)`;
}

/**
 * What stops the template being built, or null: every channel is needed, and
 * the elevator channel — whose calls the modifier holds — can be neither the
 * relay nor the read-back channel. The relay may read itself back.
 */
export function templateProblem(
  elevatorChannelId: string,
  relayChannelId: string,
  discreteChannelId: string,
): string | null {
  if (!elevatorChannelId || !relayChannelId || !discreteChannelId) {
    return "Pick the elevator, relay and read-back channels.";
  }
  if (elevatorChannelId === relayChannelId || elevatorChannelId === discreteChannelId) {
    return "The elevator channel cannot also be the relay or the read-back channel.";
  }
  return null;
}

const rowStyle: React.CSSProperties = { marginBottom: 12 };

/** The Secured-Floor Template button and its dialog. */
export default function SecuredFloorTemplateModal({
  channels,
  onApply,
}: SecuredFloorTemplateModalProps): React.JSX.Element {
  const [t] = useTranslation();
  const [open, setOpen] = useState(false);
  const [variant, setVariant] = useState<SecuredFloorVariant>("front");
  const [securityLeaf, setSecurityLeaf] = useState<Control>(Control.SecureCarCall);
  const [elevatorChannelId, setElevatorChannelId] = useState("");
  const [relayChannelId, setRelayChannelId] = useState("");
  const [discreteChannelId, setDiscreteChannelId] = useState("");
  const [name, setName] = useState(defaultTemplateName("front"));

  const channelOptions = channels.map((c) => ({ label: c.name, value: c.id }));
  const problem = templateProblem(elevatorChannelId, relayChannelId, discreteChannelId);

  const changeVariant = (next: SecuredFloorVariant) => {
    // Keep a typed name; follow the call only while the name is the default.
    if (name === defaultTemplateName(variant)) setName(defaultTemplateName(next));
    setVariant(next);
  };

  const apply = () => {
    if (problem) return;
    onApply(
      securedFloorModifier({
        variant,
        name: name.trim() || defaultTemplateName(variant),
        elevatorChannelId,
        relayChannelId,
        discreteChannelId,
        securityLeaf,
      }),
    );
    setOpen(false);
  };

  return (
    <>
      <Button
        data-doc-label="Secured-Floor Template"
        data-testid="modifier-template-open"
        onClick={() => setOpen(true)}
        style={{ marginBottom: 16 }}
      >
        {t("Secured-Floor Template")}
      </Button>
      <Modal
        open={open}
        title={t("Secured-floor interlock")}
        okText={t("Load into Form")}
        okButtonProps={{ disabled: problem !== null }}
        onOk={apply}
        onCancel={() => setOpen(false)}
        destroyOnClose
      >
        <p>
          {t(
            "Builds a Before modifier that unsecures a floor for an energized car call, releases the call, follows the car through its door cycle and secures the floor again. It loads into the form unsaved: review it, then Add Modifier.",
          )}
        </p>
        <div style={rowStyle}>
          <span className="generalStyles__inputLabel">{t("Call")}</span>
          <div style={{ marginTop: 4 }}>
            <Radio.Group
              data-doc-label="Template Call"
              data-testid="modifier-template-call"
              value={variant}
              onChange={(e) => changeVariant(e.target.value as SecuredFloorVariant)}
            >
              <Radio data-doc-label="Front Call" value="front">
                Front call
              </Radio>
              <Radio data-doc-label="Rear Call" value="rear">
                Rear call
              </Radio>
            </Radio.Group>
          </div>
        </div>
        <div style={rowStyle}>
          <span className="generalStyles__inputLabel">{t("Elevator Channel")}</span>
          <Select
            data-doc-label="Template Elevator Channel"
            data-testid="modifier-template-elevator"
            style={{ width: "100%", marginTop: 4 }}
            placeholder={t("The LN1 channel the car calls arrive on")}
            value={elevatorChannelId || undefined}
            options={channelOptions}
            onChange={(v: string) => setElevatorChannelId(v ?? "")}
          />
        </div>
        <div style={rowStyle}>
          <span className="generalStyles__inputLabel">{t("Relay Channel")}</span>
          <Select
            data-doc-label="Template Relay Channel"
            data-testid="modifier-template-relay"
            style={{ width: "100%", marginTop: 4 }}
            placeholder={t("The channel whose relays secure the floors")}
            value={relayChannelId || undefined}
            options={channelOptions}
            onChange={(v: string) => setRelayChannelId(v ?? "")}
          />
        </div>
        <div style={rowStyle}>
          <span className="generalStyles__inputLabel">{t("Read-Back Channel")}</span>
          <Select
            data-doc-label="Template Read-Back Channel"
            data-testid="modifier-template-read-back"
            style={{ width: "100%", marginTop: 4 }}
            placeholder={t("The channel whose inputs report each floor secured")}
            value={discreteChannelId || undefined}
            options={channelOptions}
            onChange={(v: string) => setDiscreteChannelId(v ?? "")}
          />
        </div>
        <div style={rowStyle}>
          <span className="generalStyles__inputLabel">{t("Security Leaf")}</span>
          <Select
            data-doc-label="Template Security Leaf"
            data-testid="modifier-template-security-leaf"
            style={{ width: "100%", marginTop: 4 }}
            value={securityLeaf}
            onChange={(v: Control) => setSecurityLeaf(v)}
            options={[
              { label: "Control::SecureCarCall", value: Control.SecureCarCall },
              { label: "Control::SecureRearCarCall", value: Control.SecureRearCarCall },
            ]}
          />
        </div>
        <div style={rowStyle}>
          <span className="generalStyles__inputLabel">{t("Name")}</span>
          <Input
            data-doc-label="Template Name"
            data-testid="modifier-template-name"
            style={{ marginTop: 4 }}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        {problem && (
          <Alert
            data-testid="modifier-template-problem"
            type="info"
            showIcon
            message={t(problem)}
          />
        )}
      </Modal>
    </>
  );
}
